import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Spinner } from "@ps-design-system/components/Spinner";
import { Text } from "@ps-design-system/components/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ps-design-system/components/Tooltip";
import DownloadIcon from "@ps-design-system/icons/Download/Download24";
import InfoIcon from "@ps-design-system/icons/Info/Info16";
import { useAsyncLoadingFn } from "@ps-design-system/lib/hooks/use-async-loading-fn";

import {
  guestVatSupportLink,
  hostVatSupportLink,
} from "../../data/supportLinks";

type InvoiceCardProps = {
  country?: string;
  day?: string;
  feeName?: string;
  invoiceId: string;
  isHost?: boolean;
  isTransactional?: boolean;
  month?: string;
  onDownloadClick: () => Promise<void>;
  title?: string;
  year?: string;
};

/**
 * Renders one downloadable invoice: a monthly summary, or a single booking
 * titled by its invoice number.
 */
const InvoiceCard = ({
  country,
  day,
  feeName,
  invoiceId,
  isHost,
  isTransactional,
  month,
  onDownloadClick,
  title,
  year,
}: InvoiceCardProps) => {
  // The design system ships this hook without an `index.d.ts`, so TS infers its
  // return from the minified JS as an array, not a tuple — restore the tuple.
  const [handleDownloadClick, isDownloading] = useAsyncLoadingFn(
    onDownloadClick,
  ) as [() => Promise<void>, boolean];

  const fullDateLine = `${month}${day ? ` ${day}` : ""}, ${year}`;

  return (
    <div className="tw-border tw-border-gray-300 tw-rounded-4 tw-p-16 tw-w-full">
      <div className="tw-flex tw-justify-between tw-items-center tw-gap-8">
        <Text typeface="body-2-m">
          {isTransactional ? (
            `#${invoiceId}`
          ) : (
            <span className="tw-flex tw-items-center tw-gap-4">
              <FormattedMessage
                id="hosting.tax_details_page.monthly_invoice"
                defaultMessage="Monthly Invoice: {date}"
                values={{ date: `${month} ${year}` }}
              />
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="tw-text-gray-600 tw-inline" />
                  <span className="tw-sr-only">
                    <FormattedMessage
                      id="tax_invoices.invoice_card.more_information"
                      defaultMessage="More information"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <FormattedMessage
                    id="inbox.tooltip_data.multiple_tax_invoices"
                    defaultMessage="If you updated your tax details, you may receive multiple invoices for a single month. {learnMore}"
                    values={{
                      learnMore: (
                        <a
                          className="tw-underline"
                          href={
                            isHost ? hostVatSupportLink : guestVatSupportLink
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FormattedMessage
                            id="inbox.tooltip_data.learn_more_service"
                            defaultMessage="Learn More"
                          />
                        </a>
                      ),
                    }}
                  />
                </TooltipContent>
              </Tooltip>
            </span>
          )}
        </Text>

        <Button
          onClick={handleDownloadClick}
          disabled={isDownloading}
          variant="ghost"
          iconOnly={true}
          testId="invoice-card-download"
        >
          {isDownloading ? <Spinner appearance="gray" /> : <DownloadIcon />}
          <span className="tw-sr-only">
            <FormattedMessage
              id="tax_invoices.invoice_card.download"
              defaultMessage="Download invoice"
            />
          </span>
        </Button>
      </div>

      <Divider className="tw-mt-16 tw-mb-8" />

      <div className="tw-flex tw-gap-4 tw-text-gray-600">
        {isTransactional && (
          <>
            <Text typeface="body-1-m" className="tw-text-gray-600">
              {fullDateLine}
            </Text>
            <span aria-hidden="true">&middot;</span>
          </>
        )}
        <Text typeface="body-1" className="tw-text-gray-600">
          {country}
        </Text>
        <Text typeface="body-1" className="tw-text-gray-600">
          {feeName}
        </Text>
      </div>

      {isTransactional && title && (
        <Text
          typeface="body-1"
          className="tw-text-gray-600 tw-mt-4 tw-block tw-truncate"
        >
          {title}
        </Text>
      )}
    </div>
  );
};

export { InvoiceCard };
export type { InvoiceCardProps };
